<template>
    <div class="p-6">
        <!-- Roles -->
        <p class="font-light text-2xl py-3">User Roles</p>
        <div class="flex flex-wrap">
            <role-container v-for="role in roles" 
                :key="role.id"
                :role=role
                @role-selected="roleSelected"
                @role-deselected="roleDeselected"
                :selected-role="selected_role"
                :ref="'role_container_'+role.id">
            </role-container>
        </div>

        <!-- All Tags -->
        <div class="py-3 mt-8 flex items-center">
            <span class="font-light text-2xl">
                Available Tags
            </span>
            <div v-is-authorized="{ roles: [role], permissions: ['can_manage_tags'] }"
                v-if="is_enabled"
                class="ml-4">
                <router-link :to="{name: 'manage-tags', params: {vault_id: $route.params.vault_id}}"
                    class="border rounded p-2 bg-grey-light text-grey-dark hover:bg-grey-dark hover:text-grey-light no-underline">
                    <i class="fa fa-tags"></i> Manage Tags
                </router-link>
            </div>
        </div>
        <div class="bg-grey-lighter p-3">
            <button type="button" class="border border-brand-light p-2 rounded"
                :class="{
                    'border-brand text-white bg-brand': tag_selection == 'all', 
                    'text-brand-light': tag_selection != 'all',
                    'hover:border-brand hover:text-white hover:bg-brand': selected_role != '',
                    'border-brand-lighter text-brand-lighter cursor-not-allowed opacity-75': selected_role == ''
                }"
                :disabled="selected_role == ''"
                @click.prevent="selectAll()">
                <i class="fa fa-check-square"></i> Select All
            </button>
            <button type="button" class="border border-brand-light p-2 rounded"
                :class="{
                    'border-brand text-white bg-brand': tag_selection == 'none', 
                    'text-brand-light': tag_selection != 'none', 
                    'hover:border-brand hover:text-white hover:bg-brand': selected_role != '',
                    'border-brand-lighter text-brand-lighter cursor-not-allowed opacity-75': selected_role == ''
                }"
                :disabled="selected_role == ''"
                @click.prevent="deselectAll()">
                <i class="fa fa-square"></i> Deselect All
            </button>
        </div>
        <div class="flex flex-wrap text-center border-t">
            <div v-for="category in categorized_tags"
                :key="category.id"
                class="flex flex-col mt-4 py-4 w-full sm:w-1/2 lg:w-1/3 xl:w-1/6">
                <p class="text-brand font-medium text-lg uppercase h-8 text-center" v-if="category.name != ''">{{category.name}}</p>
                <p class="text-brand font-medium text-lg uppercase h-8 text-center" v-else>&nbsp;</p>
                <div v-for="tag in category.tags" 
                    :key="tag.id"
                    class="p-4 text-left flex items-center">
                    <toggle-button class="pr-2 flex-none"
                        :value="isAssigned(tag)" 
                        :sync="true" 
                        :labels="false"
                        :width="35"
                        @change="assignTag(tag)"
                        :disabled="selected_role == ''"/>
                    <p class="flex-1">{{tag.abbv}}</p>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import RoleContainer from "../../components/RolesRestrictions/RoleContainer";
import Tags from "../../components/Tagging/Tags";
import Tag from "../../components/Tagging/Tag";

export default {
  components: {
    RoleContainer,
    Tags,
    Tag
  },

  data() {
    return {
      roles: {},
      categorized_tags: {},

      selected_role: "",
      selected_role_assigned_tags: "",

      tag_selection: ""
    };
  },

  mounted() {
    this.getRoles();
    this.getVaultTags();
  },

  methods: {
    getRoles() {
      axios.get(`api/vaults/${this.$route.params.vault_id}/roles`).then(resp => {
        this.roles = resp.data;
      });
    },

    getVaultTags() {
      axios.get(`api/vaults/${this.$route.params.vault_id}/categories`).then(({ data }) => {
        this.categorized_tags = data;
      });
    },

    roleSelected(payload) {
      this.selected_role = payload.role;

      axios.get(`api/vaults/${this.$route.params.vault_id}/roles/${this.selected_role.id}/tags`).then(({ data }) => {
        this.selected_role_assigned_tags = data;
      });
    },

    roleDeselected(payload) {
      this.selected_role = "";
      this.selected_role_assigned_tags = "";
    },

    isAssigned(tag) {
      if (!_.isEmpty(this.selected_role_assigned_tags)) {
        let assigned = _.find(this.selected_role_assigned_tags, { id: tag.id });

        if (_.isEmpty(assigned)) {
          return false;
        } else {
          return true;
        }
      }
    },

    assignTag(value) {
      let is_assigned = this.isAssigned(value);
      let tags = "";
      if (is_assigned) {
        tags = _.map(
          _.filter(this.selected_role_assigned_tags, function(t) {
            return t.id !== value.id;
          }),
          "id"
        );
      } else {
        tags = _.map(this.selected_role_assigned_tags, "id");
        tags.push(value.id);
      }

      this.updateTags(tags);
    },

    selectAll() {
      this.tag_selection = "all";

      let tags = _.flatten(
        _.map(this.categorized_tags, function(t) {
          return _.map(t.tags, "id");
        })
      );

      this.updateTags(tags);
    },

    deselectAll() {
      this.tag_selection = "none";

      this.updateTags([]);
    },

    updateTags(tags) {
      axios
        .put(`api/vaults/${this.$route.params.vault_id}/roles/${this.selected_role.id}/tags`, { tags })
        .then(({ data }) => {
          this.$noty.success(`Tags for ${this.selected_role.title} has been updated!`, {
            killer: true,
            timeout: 1500
          });

          _.delay(() => {
            this.$ebus.$emit("get-categorized-tags");
          }, 800);

          this.selected_role_assigned_tags = data.tags;
          let ref_name = "role_container_" + this.selected_role.id;
          this.$refs[ref_name][0].getMyTagsCount();
        })
        .catch(({ response }) => {
          if (!_.isNil(response.data)) {
            this.$noty.error(response.data.message, {
              killer: true,
              timeout: 1500
            });
          }
        });
    }
  },

  computed: {
    ...mapGetters({
      role: "getUserRole"
    }),

    is_enabled() {
      return window.enable_tag_editing;
    }
  }
};
</script>

<style>
</style>
